import './types.d.ts';

import { createTheme } from '@mui/material';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { THEME_MODE } from '@/constants/storage-flags.ts';
import { theme as dsTheme } from '@/design-system';

import { colors } from './colors.ts';
import components from './overrides/index.ts';
import { typographyOverrides } from './overrides/typography.ts';

type ThemeState = {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'dark',
      toggleTheme: () =>
        set((state) => {
          const newMode = state.mode === 'light' ? 'dark' : 'light';
          return { mode: newMode };
        }),
    }),
    { name: THEME_MODE },
  ),
);

/**
 * The one MUI theme the app runs on. Tests and Storybook build theirs from here too: the
 * `components` overrides force `MuiStack` to `flexDirection: row` and read custom palette keys, so
 * a harness assembling its own bare theme renders layouts the opposite way from production.
 */
export const createAppTheme = (mode: ThemeState['mode'] = 'dark') => {
  const muiTheme = createTheme({
    palette: {
      mode,
      ...colors[mode],
    },
    typography: typographyOverrides,
    components,
  });

  Object.assign(muiTheme, { colors: dsTheme.colors, radius: dsTheme.radius });
  Object.assign(muiTheme.spacing, dsTheme.spacing);
  Object.assign(muiTheme.shadows, dsTheme.shadows);

  return muiTheme;
};

export const useCustomTheme = () => {
  const { mode } = useThemeStore();

  return createAppTheme(mode);
};
